import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, RouterOutlet, NavigationEnd } from '@angular/router';
import { CommonModule, DatePipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { 
  HeaderComponent, 
  NavComponent, 
  FooterComponent,
  LayoutService,
  NavigationItem
} from '@cvx/nextpage';
import { CalAngularService } from '@cvx/cal-angular';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { NzAvatarModule } from 'ng-zorro-antd/avatar';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { filter, switchMap } from 'rxjs';

@Component({
  selector: 'app-layout',
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    HeaderComponent,
    NavComponent,
    FooterComponent,
    NzDropDownModule,
    NzAvatarModule,
    NzIconModule
  ],
  templateUrl: './layout.component.html',
  styleUrl: './layout.component.css'
})
export class LayoutComponent implements OnInit, OnDestroy {
  navigationItems: NavigationItem[] = [
    { label: 'home', link: '/home' },
    { label: 'all assessments', link: '/assessments' }
  ];

  userName = '';
  userRole = '';
  userInitials = '';
  userProfileImage = 'assets/images/default-profile.svg';
  lastSynced = '';
  breadcrumb = 'home';

  constructor(
    private layout: LayoutService,
    private calService: CalAngularService,
    private router: Router,
    private http: HttpClient
  ) {}

  ngOnInit(): void {
    this.layout.setClass('layout-12');
    this.loadUserInfo();
    this.lastSynced = new DatePipe('en-US').transform(new Date(), 'MM/dd/yy h:mm a') ?? '';
    this.updateBreadcrumb(this.router.url);
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        const url = event.urlAfterRedirects;
        this.updateBreadcrumb(url);
      });
  }

  private updateBreadcrumb(url: string): void {
    if (url.includes('/assessments/create')) {
      this.breadcrumb = 'all assessments > create assessment';
    } else if (url.includes('/assessments')) {
      this.breadcrumb = 'all assessments';
    } else if (url.includes('/error')) {
      this.breadcrumb = 'error';
    } else if (url.includes('/home') || url === '/') {
      this.breadcrumb = 'home';
    }
  }

  private loadUserInfo(): void {
    try {
      const account = this.calService.getAccount();
      if (account) {
        this.userName = account.name ?? 'User';
      } else {
        this.userName = 'Jonathan Doe';
        this.userRole = 'Impact Leader';
        this.userInitials = 'JD';
      }
    } catch {
      this.userName = 'Jonathan Doe';
      this.userRole = 'Impact Leader';
    }
    if (!this.userRole) {
      this.userRole = 'User';
    }
    this.userProfileImage = 'assets/images/default-profile.svg';
    this.fetchProfilePicture();
    const parts = this.userName.split(' ');
    this.userInitials = ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase();
  }

  private fetchProfilePicture(): void {
    this.calService.getAADToken().pipe(
      switchMap(token => {
        return this.http.get(
          'https://graph.microsoft.com/v1.0/me/photo/$value',
          { 
            responseType: 'blob',
            headers: { Authorization: `Bearer ${token}` }
          }
        );
      })
    ).subscribe({
      next: (res: Blob) => {
        if (res && res.size > 0) {
          const imageUrl = URL.createObjectURL(res);
          this.userProfileImage = imageUrl;
        } else {
          this.userProfileImage = 'assets/images/default-profile.svg';
        }
      },
      error: (err: any) => {
        if (err?.status !== 404) {
          console.debug('Profile picture unavailable - using default avatar');
        }
        this.userProfileImage = 'assets/images/default-profile.svg';
      }
    });
  }

  ngOnDestroy(): void {
    if (this.userProfileImage && this.userProfileImage.startsWith('blob:')) {
      URL.revokeObjectURL(this.userProfileImage);
    }
  }
}
